import { TokenResponse } from '@/@types/responses/miscellaneous/TokenResponse'
import { AppConfig } from '@/app.config'
import authenticatedFetch from '@/authenticatedFetch'
import { LOCAL_STORAGE_CONSTANTS } from '@/constants/localstorage.contstants'
import { ChangePasswordMessage } from '@/@types/messages/ChangePasswordMessage'
import { ConfirmEmailMessage } from '@/@types/messages/ConfirmEmailMessage'
import { ForgotPasswordMessage } from '@/@types/messages/ForgotPasswordMessage'
import { LoginMessage } from '@/@types/messages/LoginMessage'
import { RefreshAccessTokenMessage } from '@/@types/messages/RefreshAccessTokenMessage'
import { CreateAccountMessage } from '@/@types/messages/CreateAccountMessage'
import { ResetPasswordMessage } from '@/@types/messages/ResetPasswordMessage'
import { HandleUserNeedingEmailConfirmationMessage } from '@/@types/messages/HandleUserNeedingEmailConfirmationMessage'
import { AccountInformationDetails } from '@/@types/responses/miscellaneous/AccountInformationDetails'
import { AccountInformationMessage } from '@/@types/messages/AccountInformationMessage'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export class MiscellaneousService {
  private appConfig: AppConfig

  constructor() {
    this.appConfig = new AppConfig()
  }

  public changePassword(changePasswordMessage: ChangePasswordMessage): Promise<void> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(changePasswordMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/change-password`, this, requestOptions).then(() => {
      return Promise.resolve()
    })
  }

  public confirmEmail(confirmEmailMessage: ConfirmEmailMessage): Promise<void> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(confirmEmailMessage)
    }

    return fetch(`${this.appConfig.sacServicesUrl}/v1/confirm-email`, requestOptions).then(async response => {
      if (response.ok)
        return Promise.resolve()

      const errorsResponse = await response.json()
      return Promise.reject(new ErrorsResponse(errorsResponse.errors))
    })
  }

  public createAccount(createAccountMessage: CreateAccountMessage): Promise<void> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(createAccountMessage)
    }

    return fetch(`${this.appConfig.sacServicesUrl}/v1/my-account`, requestOptions).then(async response => {
      if (response.ok)
        return Promise.resolve()

      const errorsResponse = await response.json()
      return Promise.reject(new ErrorsResponse(errorsResponse.errors))
    })
  }

  public deleteAccount(): Promise<void> {
    const requestOptions = {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' }
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/my-account`, this, requestOptions).then(() => {
      return Promise.resolve()
    })
  }

  public getAccountInformation(): Promise<AccountInformationDetails> {
    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/my-account`, this).then(async response => {
      const json = await response.json()
      return new AccountInformationDetails(json.hasCustomerRecord, json.firstName, json.lastName, json.phoneNumber, json.addressLine1, json.addressLine2, json.city, json.stateId, json.postalCode)
    })
  }

  public handleUserForgotPassword(forgotPasswordMessage: ForgotPasswordMessage): Promise<void> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(forgotPasswordMessage)
    }

    return fetch(`${this.appConfig.sacServicesUrl}/v1/forgot-password`, requestOptions).then(async response => {
      if (response.ok)
        return Promise.resolve()

      const errorsResponse = await response.json()
      return Promise.reject(new ErrorsResponse(errorsResponse.errors))
    })
  }

  public handleUserNeedingEmailConfirmation(handleUserNeedingEmailConfirmationMessage: HandleUserNeedingEmailConfirmationMessage): Promise<void> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(handleUserNeedingEmailConfirmationMessage)
    }

    return fetch(`${this.appConfig.sacServicesUrl}/v1/handle-user-needing-email-confirmation`, requestOptions).then(async response => {
      if (response.ok)
        return Promise.resolve()

      const errorsResponse = await response.json()
      return Promise.reject(new ErrorsResponse(errorsResponse.errors))
    })
  }

  public loginUser(loginMessage: LoginMessage): Promise<TokenResponse> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(loginMessage)
    }

    return fetch(`${this.appConfig.sacServicesUrl}/v1/login`, requestOptions).then(async response => {
      const json = await response.json()

      if (response.ok) {
        const tokenResponse = new TokenResponse(json.accessToken, json.isEmployee, json.refreshToken)
        localStorage.setItem(LOCAL_STORAGE_CONSTANTS.accessToken, tokenResponse.accessToken)
        localStorage.setItem(LOCAL_STORAGE_CONSTANTS.isEmployee, String(tokenResponse.isEmployee))
        localStorage.setItem(LOCAL_STORAGE_CONSTANTS.refreshToken, tokenResponse.refreshToken)
        window.dispatchEvent(new CustomEvent(LOCAL_STORAGE_CONSTANTS.eventName))

        return tokenResponse
      } 
      
      return Promise.reject(new ErrorsResponse(json.errors))
    })
  }

  public logoutUser(): Promise<void> {
    const requestOptions = {
      method: 'POST'
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/logout`, this, requestOptions).then(() => {
      localStorage.setItem(LOCAL_STORAGE_CONSTANTS.accessToken, '')
      localStorage.setItem(LOCAL_STORAGE_CONSTANTS.isEmployee, 'false')
      localStorage.setItem(LOCAL_STORAGE_CONSTANTS.refreshToken, '')
      window.dispatchEvent(new CustomEvent(LOCAL_STORAGE_CONSTANTS.eventName))

      return Promise.resolve()
    })
  }

  public refreshAccessToken(returnUrl: string): Promise<void> {
    const refreshAccessTokenMessage = new RefreshAccessTokenMessage(localStorage.getItem(LOCAL_STORAGE_CONSTANTS.accessToken), localStorage.getItem(LOCAL_STORAGE_CONSTANTS.refreshToken))
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(refreshAccessTokenMessage)
    }

    return fetch(`${this.appConfig.sacServicesUrl}/v1/refresh-access-token`, requestOptions).then(async response => {
      const json = await response.json()
      if (response.ok) {
        const tokenResponse = new TokenResponse(json.accessToken, json.isEmployee, json.refreshToken)

        localStorage.setItem(LOCAL_STORAGE_CONSTANTS.accessToken, tokenResponse.accessToken)
        localStorage.setItem(LOCAL_STORAGE_CONSTANTS.isEmployee, String(tokenResponse.isEmployee))
        localStorage.setItem(LOCAL_STORAGE_CONSTANTS.refreshToken, tokenResponse.refreshToken)
        window.dispatchEvent(new CustomEvent(LOCAL_STORAGE_CONSTANTS.eventName))

        return Promise.resolve()
      } else {
        if (window.location.href !== '') {
          localStorage.setItem(LOCAL_STORAGE_CONSTANTS.accessToken, '')
          localStorage.setItem(LOCAL_STORAGE_CONSTANTS.isEmployee, 'false')
          localStorage.setItem(LOCAL_STORAGE_CONSTANTS.refreshToken, '')
          window.dispatchEvent(new CustomEvent(LOCAL_STORAGE_CONSTANTS.eventName))
          window.location.href = `/?returnUrl=${encodeURIComponent(returnUrl)}`
        }
  
        return Promise.reject(new ErrorsResponse(json.errors))
      }
    })
  }

  public resetPassword(resetPasswordMessage: ResetPasswordMessage): Promise<void> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(resetPasswordMessage)
    }

    return fetch(`${this.appConfig.sacServicesUrl}/v1/reset-password`, requestOptions).then(async response => {
      if (response.ok)
        return Promise.resolve()

      const errorsResponse = await response.json()
      return Promise.reject(new ErrorsResponse(errorsResponse.errors))
    })
  }

  public updateAccountInformation(accountInformationMessage: AccountInformationMessage): Promise<void> {
    const requestOptions = {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(accountInformationMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/my-account`, this, requestOptions).then(() => {
      return Promise.resolve()
    })
  }
}
